import React, { Fragment, Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  showGlobalModal,
  hideGlobalModal,
  getAccountInfo,
  signOut,
} from './store/actions';
import hoistStatics from 'hoist-non-react-statics';
import isEmpty from 'lodash/isEmpty';
import GlobalModals from 'shared/lib/components/GlobalModals';
import ErrorPage from 'shared/lib/components/ErrorPage';
import Spinner from 'shared/lib/components/Spinner';
import { logException } from 'shared/lib/utils';
import * as analytics from 'shared/lib/analytics';
import { verifyTokenValidity } from './services/verifyTokenValidity';

const mapStateToProps = state => {
  return {
    activeGlobalModal: state.activeGlobalModal,
    globalModalProps: state.globalModalProps,
    isLoggedIn: !!state.authToken,
    account: state.account,
    token: state.authToken,
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getAccountInfo,
      showGlobalModal,
      hideGlobalModal,
      signOut,
    },
    dispatch
  );

export default function withGlobals(opts = {}) {
  return WrappedComponent => {
    class ErrorHandlerComponent extends Component {
      state = {
        hasError: false,
        displayLoadingSpinner: false,
      };

      displayName = `withGlobals(${WrappedComponent.displayName ||
        WrappedComponent.name})`;
      WrappedComponent = WrappedComponent;
      componentDidMount() {
        this.timer = setTimeout(this.enableSpinner, 200);
        verifyTokenValidity(this.props.token)
          .then(isValid => {
            if (!isValid) {
              return this.props.signOut();
            }
            if (this.props.isLoggedIn && isEmpty(this.props.account)) {
              this.props.getAccountInfo();
            }
          })
          .catch(() => {
            return this.props.signOut();
          })
          .finally(() => {
            if (!opts.analyticsPrefix) {
              console.warn(
                `${this.displayName} option analyticsPrefix is missing`
              );
            }
          });
      }

      componentWillUnmount() {
        clearTimeout(this.timer);
      }

      componentDidCatch(error, info) {
        logException(error, info);
        this.setState({
          hasError: true,
        });
      }

      enableSpinner = () => {
        this.setState({ displayLoadingSpinner: true });
      };

      trackEventWithPrefix(event) {
        const eventName = [opts.analyticsPrefix, event].join(' - ');
        const eventParams = {
          noninteraction: 1,
        };
        analytics.track(eventName, eventParams);
      }

      handleError = (error, info) => {
        logException(error, info);
        this.props.showGlobalModal({
          id: 'error',
          error,
          info,
        });
        switch (error.code) {
          case 'no_offers_found':
            this.trackEventWithPrefix('No Offers Found');
            break;
          case 'property_address_invalid':
          case 'ZERO_RESULTS':
            this.trackEventWithPrefix('Invalid Address');
            break;
          case 'quote_request_rejected':
            this.trackEventWithPrefix('Quote Ineligible');
            break;
          default:
            break;
        }
      };

      get contentNode() {
        const { isLoggedIn, account } = this.props;
        if (this.state.hasError) {
          return <ErrorPage />;
        } else if (
          isLoggedIn &&
          isEmpty(account) &&
          !this.state.displayLoadingSpinner
        ) {
          return null;
        } else if (
          isLoggedIn &&
          isEmpty(account) &&
          this.state.displayLoadingSpinner
        ) {
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Spinner />
              <h2>Loading...</h2>
            </div>
          );
        } else {
          return (
            <WrappedComponent onError={this.handleError} {...this.props} />
          );
        }
      }

      render() {
        const {
          activeGlobalModal,
          globalModalProps,
          hideGlobalModal,
        } = this.props;
        return (
          <Fragment>
            {this.contentNode}
            <GlobalModals
              activeModal={activeGlobalModal}
              onDismiss={hideGlobalModal}
              {...globalModalProps}
            />
          </Fragment>
        );
      }
    }

    const ComponentWithStatics = hoistStatics(
      ErrorHandlerComponent,
      WrappedComponent
    );
    return connect(
      mapStateToProps,
      mapDispatchToProps
    )(ComponentWithStatics);
  };
}
